import React, { useState, useMemo } from "react"
import { useDispatch, useSelector } from "react-redux"
import { createSelector } from "reselect"

import "./index.scss"

import { Popup } from "components"
import { UsabilityConfig } from "components/Config"
import Button from "react-bootstrap/Button"
import { FiSettings, FiArrowLeft } from "react-icons/fi"
import { FaPlus } from "react-icons/fa"
import { assets, message } from "state_management"

// Configuration for instrument usability.
export default function AssetUsabilityConfig({ assetId, onClose }) {
  const dispatch = useDispatch()
  const asset = useSelector((state) => state.asset.graphInfo[assetId])

  // Memoized selector for maintenance items
  const selectMaintenanceItems = useMemo(() => {
    return createSelector(
      [(state) => state.asset.graphInfo[assetId]?.maintenance],
      (maintenance) => {
        if (!maintenance) return [];

        // Handle new format (object with IDs as keys)
        if (!Array.isArray(maintenance)) {
          return Object.entries(maintenance).map(([id, item]) => ({
            ...item,
            id
          }));
        }
        // Handle legacy format (array)
        return maintenance.map(item => ({
          ...item,
          id: item.id
        }));
      }
    );
  }, [assetId]);

  // Use the memoized selector
  const maintenanceItems = useSelector(selectMaintenanceItems)

  // State to track which maintenance item is being edited or if we're creating a new one
  const [editingItem, setEditingItem] = useState(null)
  const [isCreating, setIsCreating] = useState(false)

  // Helper function to format recurrence text
  const formatRecurrence = (item) => {
    // Make unit plural if value is not 1
    const unitText = item.recurrenceValue === 1 ? item.recurrenceUnit : `${item.recurrenceUnit}s`;
    return `${item.recurrenceValue} ${unitText}`;
  }

  // Handle clicking on the settings icon to edit a maintenance item
  const handleEditItem = (item) => {
    setIsCreating(false);
    setEditingItem(item);
  }

  // Handle clicking the add button to create a new maintenance item
  const handleCreateItem = () => {
    setIsCreating(true);
    setEditingItem(null);
  }

  // Handle closing the UsabilityConfig popup
  const handleCloseUsabilityConfig = () => {
    setEditingItem(null);
    setIsCreating(false);
  }

  // Handle marking a maintenance item as done
  const handleMarkAsDone = (item) => {
    dispatch(assets.completeMaintenance(item.id, assetId))
      .then(() => {
        // Refresh asset graph info after completing maintenance
        dispatch(assets.fetchGraphInfo());
        dispatch(message.success(`Maintenance "${item.name}" marked as completed`));
      })
      .catch(() => {
        dispatch(message.error(`Failed to mark maintenance "${item.name}" as completed`));
      });
  }

  // Handle saving changes from the UsabilityConfig
  const handleSaveUsabilityConfig = (maintenanceData) => {
    if (isCreating) {
      // Create a new maintenance item
      dispatch(assets.createMaintenance(assetId, maintenanceData))
        .then(() => {
          // Refresh asset graph info after creating maintenance
          dispatch(assets.fetchGraphInfo());
          dispatch(message.success(`Maintenance "${maintenanceData.name}" created successfully`));
        })
        .catch(() => {
          dispatch(message.error(`Failed to create maintenance "${maintenanceData.name}"`));
        });
    } else {
      // Update an existing maintenance item
      dispatch(assets.updateMaintenance(maintenanceData.id, maintenanceData))
        .then(() => {
          // Refresh asset graph info after updating maintenance
          dispatch(assets.fetchGraphInfo());
          dispatch(message.success(`Maintenance "${maintenanceData.name}" updated successfully`));
        })
        .catch(() => {
          dispatch(message.error(`Failed to update maintenance "${maintenanceData.name}"`));
        });
    }

    setEditingItem(null);
    setIsCreating(false);
  }

  const handleDelete = (item) => {
    dispatch(assets.deleteMaintenance(item.id, assetId))
      .then(() => {
        // Refresh asset graph info after deleting maintenance
        dispatch(assets.fetchGraphInfo());
        dispatch(message.success(`Maintenance "${item.name}" deleted successfully`));
      })
      .catch(() => {
        dispatch(message.error(`Failed to delete maintenance "${item.name}"`));
      });

    setEditingItem(null);
    setIsCreating(false);
  }

  // If we're creating or editing an item, show the UsabilityConfig component
  if (isCreating || editingItem) {
    return (
      <Popup nonScrollable onCancel={onClose}>
        <Button className="go-back-button" variant="secondary" onClick={handleCloseUsabilityConfig}>
          <FiArrowLeft />
        </Button>
        <UsabilityConfig
          maintenanceItem={editingItem}
          assetName={asset?.name}
          onCancel={handleCloseUsabilityConfig}
          onSave={handleSaveUsabilityConfig}
          onDelete={handleDelete}
          isCreating={isCreating}
        />
      </Popup>
    );
  }

  return (
    <Popup title={asset?.name} nonScrollable onCancel={onClose}>
      <div className="usability-config">
        <table className="maintenance-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>End date</th>
              <th>Recurrence</th>
              <th></th> {/* Column for Done button */}
              <th></th> {/* Column for settings icon */}
            </tr>
          </thead>
          <tbody>
            {maintenanceItems.map((item) => {
              return (
                <tr key={item.id}>
                  <td>{item.name}</td>
                  <td>{item.endDate}</td>
                  <td>{formatRecurrence(item)}</td>
                  <td className="done-cell">
                    <Button
                      className="done-button"
                      variant="primary"
                      onClick={() => handleMarkAsDone(item)}
                    >
                      Done
                    </Button>
                  </td>
                  <td>
                    <FiSettings className="settings-icon" onClick={() => handleEditItem(item)} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <div className="add-maintenance">
          <Button
            className="add-button"
            variant="primary"
            onClick={handleCreateItem}
          >
            <FaPlus /> Add maintenance
          </Button>
        </div>
      </div>
    </Popup>
  );
}
